/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import {
  Accordion,
  AccordionGroup,
  AccordionHeader,
  AccordionPanel,
  Radio,
  RadioGroup,
  StandaloneCheckbox,
} from '@coursera/cds-core';

import PretranslatedListItem from 'bundles/coursera-plus-landing-page/editorTool/PretranslatedListItem';
import { useFeaturesContext } from 'bundles/coursera-plus-landing-page/editorTool/contextHelper';

const styles = {
  error: () => css`
    background-color: pink;
  `,
  choices: () => css`
    background-color: #eee;
    padding: 16px;
    margin-bottom: 16px;
  `,
  sortCheckbox: () => css`
    margin-left: 70px;
  `,
};

type ReferenceListProps = {
  refKeys: string[];
  sorted: boolean;
};

const ReferenceList = (props: ReferenceListProps) => {
  const context = useFeaturesContext();
  if (!context) {
    return null;
  }
  const { codedMap, pretranslatedFromCTA } = context;
  const { refKeys } = props;
  const refKeysDisplayed = props.sorted ? [...refKeys].sort() : refKeys;
  return (
    <AccordionGroup>
      {refKeysDisplayed.map((k) => {
        const translation = codedMap[k];
        const pages = pretranslatedFromCTA[k]?.pages;
        const supportText = translation?.message ?? 'no translation in the code';
        return (
          <Accordion key={k}>
            <AccordionHeader css={translation ? null : styles.error} label={k} supportText={supportText} />
            <AccordionPanel>
              <PretranslatedListItem key={k} translationKey={k} translation={translation} pages={pages} />
            </AccordionPanel>
          </Accordion>
        );
      })}
    </AccordionGroup>
  );
};

const PretranslatedReferences = () => {
  const SHOW_CODE = 'showCode';
  const SHOW_CONTENTFUL = 'showContentful';
  const [keyChoice, setKeyChoice] = useState(SHOW_CONTENTFUL);
  const [sorted, setSorted] = useState(false);

  const context = useFeaturesContext();
  const pretranslatedFromCTA = context?.pretranslatedFromCTA;
  if (!pretranslatedFromCTA) {
    return null;
  }
  const refKeys = Object.keys(pretranslatedFromCTA);

  const handleSelectedKeysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyChoice(event.target.value);
  };

  return (
    <div>
      <div css={styles.choices}>
        <RadioGroup
          label="Translation keys displayed"
          name="keys-displayed"
          onChange={handleSelectedKeysChange}
          defaultValue={SHOW_CONTENTFUL}
        >
          <Radio label="Translations in the code" value={SHOW_CODE} />
          <Radio label="Translations from Contentful" value={SHOW_CONTENTFUL} />
        </RadioGroup>
        <StandaloneCheckbox
          checked={sorted}
          label="Sort translation keys"
          value="agree"
          onChange={() => setSorted(!sorted)}
          css={styles.sortCheckbox}
        />
        {keyChoice === SHOW_CONTENTFUL && <ReferenceList sorted={sorted} refKeys={refKeys} />}
        {keyChoice === SHOW_CODE && <ReferenceList sorted={sorted} refKeys={context.codedKeys} />}
      </div>
    </div>
  );
};

export default PretranslatedReferences;
